import { CopyrightFooter, Icon, LittleBankersPiggyLogo, SafeAreaSpacer } from "../components/Common.jsx";
import { t as ct } from "../lib/content.js";
import { PRIVACY, TERMS } from "../lib/legal-text.js";

export function LegalPage({ title, updated, children }) {
  return (
    <div className="max-w-lg mx-auto p-4 sm:p-6 lb-form">
      <SafeAreaSpacer />
      <div className="mb-4 flex items-center justify-between">
        <a href="/" className="text-sm font-bold text-slate-500 inline-flex items-center gap-1"><Icon id="ic-back" size={12} style={{ color: "currentColor" }} />{ct("legal.back", "Back to Little Bankers")}</a>
      </div>
      <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6">
        <div className="mb-4 flex justify-center"><LittleBankersPiggyLogo className="h-14" /></div>
        <h1 className="text-xl font-extrabold text-slate-900 text-center mb-1">{title}</h1>
        <p className="text-xs text-slate-400 text-center mb-6">Last updated: {updated}</p>
        <div className="flex flex-col gap-4 text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
      <CopyrightFooter />
    </div>
  );
}


export function LegalSection({ n, title, children }) {
  return (
    <div>
      <h2 className="text-sm font-bold text-slate-900 mb-1">{n}. {title}</h2>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}


// LB-8 Part 2 (6 Oct 2026): the wording lives in src/lib/legal-text.js, one source for this
// renderer and the worker's public pages at /terms and /privacy. The markup below is what the
// pages had before the move (paragraphs, a bold lead, the bulleted list), drawn from the data.
function LegalBlock({ block }) {
  if (block.list) {
    return (
      <ul className="list-disc pl-5 flex flex-col gap-1">
        {block.list.map((item) => <li key={item.lead}><b>{item.lead}</b> {item.text}</li>)}
      </ul>
    );
  }
  if (block.lead) return <p><b>{block.lead}</b> {block.p}</p>;
  return <p>{block.p}</p>;
}

export function LegalDocument({ doc }) {
  return (
    <LegalPage title={doc.title} updated={doc.updated}>
      {doc.intro.map((block, i) => <LegalBlock key={`intro-${i}`} block={block} />)}
      {doc.sections.map((section) => (
        <LegalSection key={section.n} n={section.n} title={section.title}>
          {section.blocks.map((block, i) => <LegalBlock key={i} block={block} />)}
        </LegalSection>
      ))}
    </LegalPage>
  );
}

export function TermsPage() {
  return <LegalDocument doc={TERMS} />;
}

export function PrivacyPage() {
  // W1 journey-review pass (27 Aug): child photos named explicitly wherever they arise
  // (§2, §3), the interim photo-link access position stated honestly (§3 — review 4.6:
  // photo URLs are unguessable but unauthenticated until per-device access controls
  // ship), and Google Cloud added to §5 — Learn & Earn's read-aloud audio is generated
  // by Google's text-to-speech service, which §5 previously omitted. Only the word or
  // sentence being spoken is sent; no child names, profiles, or identifiers.
  return <LegalDocument doc={PRIVACY} />;
}

/* ----------------------------- wizard preview ------------------------------- */

// Lets the wizard's HTML/UI be reviewed without creating a real household or touching any
// server data: renders the exact same <SetupWizard> component (so styling stays byte-for-byte
// what a real user sees) but wired to local, throwaway React state instead of `state`/`update`
// from Root — nothing here is ever sent to the Worker or written to KV. Reached via
// littlebankers.uk/?preview=wizard, entirely separate from the normal invite/onboard/PIN flow.
